/**
 * Pure land math shared by the contract (facets), the server (ledger, plans, Today) and the web
 * (forms, map). No DOM and no Worker APIs. Every estimate here is labeled as one where it is shown.
 */
import { addDays, convertUnit, daysBetween, type Precision, type Quantity } from "../../../contract";

// ---------------------------------------------------------------- dates

/** The calendar day a move or reading counts on. Approximate precisions use their first day. */
export function dayOf(value: string | null | undefined, precision: Precision | string): string | null {
  if (!value) return null;
  if (precision === "year" || precision === "unknown") return null;
  if (precision === "month" || /^\d{4}-\d{2}$/.test(value)) return `${value.slice(0, 7)}-01`;
  return value.slice(0, 10);
}

export const maxDate = (a: string, b: string) => (a > b ? a : b);
export const minDate = (a: string, b: string) => (a < b ? a : b);

// ---------------------------------------------------------------- stays and occupancy

export interface MoveLike {
  id: string;
  on: string;               // YYYY-MM-DD
  to: string;               // place id
  from?: string | null;
  group_id?: string | null;
}

/** One animal's time in one place. `to` is null while it is still there. */
export interface Stay { place_id: string; from: string; to: string | null; event_id: string; group_id?: string }

/**
 * An animal's moves, oldest first, as stays. A move into the place it is already in continues the
 * current stay, so a repeated or "starting place" record never splits one visit into two.
 */
export function staysFromMoves(moves: MoveLike[]): Stay[] {
  const stays: Stay[] = [];
  const ordered = [...moves].map((m, i) => ({ m, i })).sort((a, b) => a.m.on.localeCompare(b.m.on) || a.i - b.i).map((x) => x.m);
  for (const m of ordered) {
    const open = stays.at(-1);
    if (open && open.to === null) {
      if (open.place_id === m.to) continue;
      open.to = m.on;
    }
    stays.push({ place_id: m.to, from: m.on, to: null, event_id: m.id, ...(m.group_id ? { group_id: m.group_id } : {}) });
  }
  return stays;
}

export interface Interval { from: string; to: string | null }

/** Merge overlapping or touching intervals into grazing periods, oldest first. */
export function mergeIntervals(list: Interval[]): Interval[] {
  const sorted = [...list].sort((a, b) => a.from.localeCompare(b.from));
  const out: Interval[] = [];
  for (const iv of sorted) {
    const last = out.at(-1);
    if (last && (last.to === null || iv.from <= last.to)) {
      if (last.to !== null) last.to = iv.to === null ? null : maxDate(last.to, iv.to);
      continue;
    }
    out.push({ ...iv });
  }
  return out;
}

/** Days of an interval inside [from, to). Open intervals run to `to`. */
export function overlapDays(iv: Interval, from: string, to: string): number {
  const start = maxDate(iv.from, from);
  const end = minDate(iv.to ?? to, to);
  return end > start ? daysBetween(start, end) : 0;
}

/** Days since a place was last grazed, or null when it is occupied or was never grazed. */
export function restDays(emptySince: string | null | undefined, today: string): number | null {
  return emptySince ? Math.max(0, daysBetween(emptySince, today)) : null;
}

// ---------------------------------------------------------------- animal units (estimates)

/** Animal units by class: a 1,000 lb cow with a calf at side is 1. Estimates, always shown as such. */
export const AU_BY_CLASS: Record<string, number> = {
  cow: 1, bull: 1.35, heifer: 0.8, steer: 0.7, yearling: 0.7, stocker: 0.6, feeder: 0.7, calf: 0.5, bull_calf: 0.5, heifer_calf: 0.5, other: 0.7,
};
const CALVES = new Set(["calf", "bull_calf", "heifer_calf"]);

/** Nursing calves (under about 8 months) count with their dams. A recorded weight beats the class figure. */
export function animalUnits(a: { class: string; age_days: number | null; weight_lb?: number | null }): number {
  if (CALVES.has(a.class) && a.age_days !== null && a.age_days < 240) return 0;
  if (a.weight_lb && a.weight_lb > 0) return Math.round((a.weight_lb / 1000) * 100) / 100;
  return AU_BY_CLASS[a.class] ?? 0.7;
}

// ---------------------------------------------------------------- forage budget (estimate)

/** Common planning figures, shown beside every budget so nobody mistakes them for measurements. */
export const DEFAULT_INTAKE_LB = 26;
export const DEFAULT_UTILIZATION_PCT = 50;
export const STALE_FORAGE_DAYS = 30;

export const FORAGE_UNITS = ["lb/ac", "kg/ha"] as const;
const KG_HA_TO_LB_AC = 0.892179;

export function forageLbPerAcre(q: Quantity | null | undefined): number | null {
  if (!q || !Number.isFinite(q.value)) return null;
  if (q.unit === "lb/ac") return q.value;
  if (q.unit === "kg/ha") return q.value * KG_HA_TO_LB_AC;
  return null;
}

export function areaAcres(q: Quantity | null | undefined): number | null {
  if (!q || !Number.isFinite(q.value)) return null;
  return convertUnit(q.value, q.unit, "ac");
}

export interface BudgetInput {
  mass?: Quantity | null;
  observed_on?: string | null;
  area_ac?: number | null;
  utilization_pct?: number | null;
  residual?: Quantity | null;
  intake_lb?: number | null;
  au: number;
  days?: number | null;
  today: string;
}

export interface Budget {
  ok: boolean;
  /** Why no budget could be worked out, in plain terms the web translates. */
  reason?: "no_forage" | "no_area" | "no_animals" | "nothing_left" | "units";
  available_aud?: number;
  needed_aud?: number;
  days_supported?: number;
  usable_lb_per_ac?: number;
  stale: boolean;
  age_days?: number;
  assumptions: { utilization_pct?: number; residual_lb_per_ac?: number; intake_lb: number; area_ac?: number; mass_lb_per_ac?: number; observed_on?: string };
}

/**
 * Animal-unit days a pasture can carry: usable forage (above the residual, or the planned share of
 * what stands) times area, divided by daily intake. Zero or negative usable forage gives an
 * explanation, never an unlimited number of days.
 */
export function forageBudget(input: BudgetInput): Budget {
  const intake = input.intake_lb && input.intake_lb > 0 ? input.intake_lb : DEFAULT_INTAKE_LB;
  const assumptions: Budget["assumptions"] = { intake_lb: intake };
  const age = input.observed_on ? Math.max(0, daysBetween(input.observed_on, input.today)) : undefined;
  const stale = age !== undefined && age > STALE_FORAGE_DAYS;
  if (input.observed_on) assumptions.observed_on = input.observed_on;
  const base = { stale, ...(age !== undefined ? { age_days: age } : {}), assumptions };
  if (!input.mass) return { ok: false, reason: "no_forage", ...base };
  const mass = forageLbPerAcre(input.mass);
  if (mass === null) return { ok: false, reason: "units", ...base };
  assumptions.mass_lb_per_ac = round(mass);
  if (!input.area_ac || input.area_ac <= 0) return { ok: false, reason: "no_area", ...base };
  assumptions.area_ac = round(input.area_ac);
  let usable: number;
  if (input.residual) {
    const residual = forageLbPerAcre(input.residual);
    if (residual === null) return { ok: false, reason: "units", ...base };
    assumptions.residual_lb_per_ac = round(residual);
    usable = mass - residual;
  } else {
    const pct = input.utilization_pct && input.utilization_pct > 0 ? Math.min(input.utilization_pct, 100) : DEFAULT_UTILIZATION_PCT;
    assumptions.utilization_pct = pct;
    usable = (mass * pct) / 100;
  }
  if (usable <= 0) return { ok: false, reason: "nothing_left", ...base };
  const available = (usable * input.area_ac) / intake;
  const out: Budget = { ok: true, available_aud: round(available), usable_lb_per_ac: round(usable), ...base };
  if (input.au > 0) {
    out.days_supported = round(available / input.au, 1);
    if (input.days && input.days > 0) out.needed_aud = round(input.au * input.days);
  } else if (input.days) {
    return { ...out, ok: true };
  }
  return out;
}

function round(n: number, digits = 0): number {
  const f = 10 ** digits;
  return Math.round(n * f) / f;
}

// ---------------------------------------------------------------- grazing plans

export interface PlanStage {
  id: string;
  place_id: string;
  /** Planned day in, YYYY-MM-DD. */
  start: string;
  /** Planned days in the place. */
  days: number;
  note?: string;
  /** The operator decided not to use this step. */
  skipped?: boolean;
  /** What a forage budget suggested when the days were chosen, kept beside the choice. */
  predicted_days?: number;
  override_reason?: string;
}

export interface PlanData {
  group_id?: string;
  head?: number;
  rest_target_days?: number;
  au_per_head?: number;
  intake_lb?: number;
  utilization_pct?: number;
  residual?: Quantity;
  note?: string;
  stages?: PlanStage[];
}

export const stageEnd = (s: Pick<PlanStage, "start" | "days">): string => addDays(s.start, s.days);

const ISO = /^\d{4}-\d{2}-\d{2}$/;

/** Clean stages from stored or agent-written plan data: valid steps only, stable ids, oldest first. */
export function normalizeStages(raw: unknown): PlanStage[] {
  if (!Array.isArray(raw)) return [];
  const used = new Set<string>();
  const out: PlanStage[] = [];
  raw.forEach((r, i) => {
    if (!r || typeof r !== "object") return;
    const s = r as Record<string, unknown>;
    const place = typeof s.place_id === "string" ? s.place_id : null;
    const start = typeof s.start === "string" && ISO.test(s.start.slice(0, 10)) ? s.start.slice(0, 10) : null;
    let days = typeof s.days === "number" ? Math.round(s.days) : typeof s.days === "string" ? Math.round(Number(s.days)) : NaN;
    if ((!Number.isFinite(days) || days <= 0) && typeof s.end === "string" && start && ISO.test(s.end.slice(0, 10))) days = daysBetween(start, s.end.slice(0, 10));
    if (!place || !start || !Number.isFinite(days) || days <= 0) return;
    let id = typeof s.id === "string" && s.id ? s.id : `st${i + 1}`;
    while (used.has(id)) id = `${id}x`;
    used.add(id);
    out.push({
      id, place_id: place, start, days: Math.min(days, 366),
      ...(typeof s.note === "string" && s.note ? { note: s.note.slice(0, 500) } : {}),
      ...(s.skipped === true ? { skipped: true } : {}),
      ...(typeof s.predicted_days === "number" && s.predicted_days > 0 ? { predicted_days: Math.round(s.predicted_days * 10) / 10 } : {}),
      ...(typeof s.override_reason === "string" && s.override_reason ? { override_reason: s.override_reason.slice(0, 300) } : {}),
    });
  });
  return out.sort((a, b) => a.start.localeCompare(b.start));
}

/** Push every step from `index` on by `days`, keeping each step's length. Earlier steps stay put. */
export function shiftStages(stages: PlanStage[], index: number, days: number): PlanStage[] {
  return stages.map((s, i) => (i >= index ? { ...s, start: addDays(s.start, days) } : s));
}

/** The next free day after the last step, for adding a step at the end. */
export function nextStageStart(stages: PlanStage[], today: string): string {
  const last = stages.at(-1);
  return last ? stageEnd(last) : today;
}

// ---------------------------------------------------------------- rain

export type RainUnit = "in" | "mm";

export function rainIn(q: Quantity | null | undefined, unit: RainUnit): number | null {
  if (!q || !Number.isFinite(q.value)) return null;
  const v = convertUnit(q.value, q.unit, unit);
  return v === null ? null : v;
}

export const roundRain = (n: number, unit: RainUnit) => (unit === "mm" ? Math.round(n) : Math.round(n * 100) / 100);

/** A rain reading covers the days after `since` up to and including its own day. */
export function rainWindow(on: string, since: string | null | undefined): Interval {
  return { from: since && since < on ? addDays(since, 1) : on, to: on };
}

export function windowsOverlap(a: Interval, b: Interval): boolean {
  return a.from <= (b.to ?? "9999") && b.from <= (a.to ?? "9999");
}

// ---------------------------------------------------------------- water

export type WaterState = "ok" | "low" | "empty" | "broken";
export const WATER_SEVERITY: Record<WaterState, number> = { ok: 0, low: 1, empty: 2, broken: 2 };

// ---------------------------------------------------------------- geometry (map boundaries)

export type LngLat = [number, number];
export interface PolygonGeometry { type: "Polygon"; coordinates: LngLat[][] }
export interface MultiPolygonGeometry { type: "MultiPolygon"; coordinates: LngLat[][][] }
export type PlaceGeometry = PolygonGeometry | MultiPolygonGeometry;

const R = 6378137;
const rad = (d: number) => (d * Math.PI) / 180;

/** Geodesic area of one ring in square meters (spherical approximation, as Leaflet and turf use). */
export function ringAreaM2(ring: LngLat[]): number {
  const n = ring.length;
  if (n < 3) return 0;
  let total = 0;
  for (let i = 0; i < n; i++) {
    const [lng1, lat1] = ring[i];
    const [lng2, lat2] = ring[(i + 1) % n];
    total += rad(lng2 - lng1) * (2 + Math.sin(rad(lat1)) + Math.sin(rad(lat2)));
  }
  return Math.abs((total * R * R) / 2);
}

export function polygons(geometry: unknown): LngLat[][][] {
  const g = geometry as { type?: string; coordinates?: unknown } | null;
  if (!g || typeof g !== "object") return [];
  if (g.type === "Polygon" && Array.isArray(g.coordinates)) return [g.coordinates as LngLat[][]];
  if (g.type === "MultiPolygon" && Array.isArray(g.coordinates)) return g.coordinates as LngLat[][][];
  if (g.type === "Feature") return polygons((g as { geometry?: unknown }).geometry);
  return [];
}

/** Mapped area in acres (outer rings minus holes), or null when there is no usable boundary. */
export function mappedAcres(geometry: unknown): number | null {
  const polys = polygons(geometry);
  if (!polys.length) return null;
  let m2 = 0;
  for (const rings of polys) rings.forEach((ring, i) => { m2 += (i === 0 ? 1 : -1) * ringAreaM2(ring); });
  return m2 > 0 ? m2 / 4046.8564224 : null;
}

/** A label point inside a simple ring: the area-weighted centroid, falling back to the vertex mean. */
export function ringCenter(ring: LngLat[]): LngLat {
  let a = 0, x = 0, y = 0;
  for (let i = 0; i < ring.length; i++) {
    const [x1, y1] = ring[i];
    const [x2, y2] = ring[(i + 1) % ring.length];
    const cross = x1 * y2 - x2 * y1;
    a += cross; x += (x1 + x2) * cross; y += (y1 + y2) * cross;
  }
  if (Math.abs(a) < 1e-12) {
    const n = ring.length || 1;
    return [ring.reduce((s, p) => s + p[0], 0) / n, ring.reduce((s, p) => s + p[1], 0) / n];
  }
  return [x / (3 * a), y / (3 * a)];
}

function segmentsCross(p1: LngLat, p2: LngLat, p3: LngLat, p4: LngLat): boolean {
  const d = (a: LngLat, b: LngLat, c: LngLat) => (b[0] - a[0]) * (c[1] - a[1]) - (b[1] - a[1]) * (c[0] - a[0]);
  const d1 = d(p3, p4, p1), d2 = d(p3, p4, p2), d3 = d(p1, p2, p3), d4 = d(p1, p2, p4);
  return ((d1 > 0 && d2 < 0) || (d1 < 0 && d2 > 0)) && ((d3 > 0 && d4 < 0) || (d3 < 0 && d4 > 0));
}

export type BoundaryProblem = "too_few" | "out_of_range" | "crosses";

/** Checks a drawn boundary (corners without the closing point) before it is saved. */
export function boundaryProblem(corners: LngLat[]): BoundaryProblem | null {
  if (corners.length < 3) return "too_few";
  if (corners.some(([lng, lat]) => !Number.isFinite(lng) || !Number.isFinite(lat) || lat < -90 || lat > 90 || lng < -180 || lng > 180)) return "out_of_range";
  const n = corners.length;
  for (let i = 0; i < n; i++) {
    for (let j = i + 1; j < n; j++) {
      if (j === i + 1 || (i === 0 && j === n - 1)) continue;
      if (segmentsCross(corners[i], corners[(i + 1) % n], corners[j], corners[(j + 1) % n])) return "crosses";
    }
  }
  return null;
}

/** GeoJSON polygon from drawn corners, closed as GeoJSON requires. */
export function polygonFromCorners(corners: LngLat[]): PolygonGeometry {
  const ring = corners.map(([lng, lat]) => [Math.round(lng * 1e7) / 1e7, Math.round(lat * 1e7) / 1e7] as LngLat);
  return { type: "Polygon", coordinates: [[...ring, ring[0]]] };
}

/** Corners of a polygon's outer ring without the closing point, for editing. */
export function cornersOf(geometry: unknown): LngLat[] {
  const ring = polygons(geometry)[0]?.[0] ?? [];
  if (ring.length > 1 && ring[0][0] === ring.at(-1)![0] && ring[0][1] === ring.at(-1)![1]) return ring.slice(0, -1);
  return ring;
}
